import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";

/** Mirrors raise_server.schemas.audit.AuditEventItem 1:1 (SD2). */
export interface AuditEventItem {
  id: string;
  event_kind: string;
  decision: string | null;
  subject_id: string;
  run_id: string | null;
  pipeline_name: string | null;
  workflow_point: string | null;
  work_item_ref: string | null;
  artifact_ref: string | null;
  branch: string | null;
  commit_sha: string | null;
  actor_member_id: string | null;
  actor_kind: string | null;
  project_id: string | null;
  payload_sha256: string;
  idempotency_key: string;
  signal_schema_version: string | null;
  occurred_at: string;
  created_at: string;
}

export interface AuditEventFilters {
  projectId?: string;
  workItemRef?: string;
  eventKind?: string;
  runId?: string;
  since?: string;
  until?: string;
}

interface AuditEventListResponse {
  events: AuditEventItem[];
  count: number;
  total: number;
  earliest_event_at: string | null;
}

interface AuditQueryData {
  events: AuditEventItem[];
  total: number;
  earliestEventAt: string | null;
  forbidden: boolean;
}

// The endpoint's documented max (SD2) — pagination is out of scope; 200
// recent events is sufficient for a timeline, and `total` lets the UI
// surface "showing 200 of N" when truncated.
const LIMIT = 200;

function buildParams(filters: AuditEventFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.projectId) params.set("project_id", filters.projectId);
  if (filters.workItemRef) params.set("work_item_ref", filters.workItemRef);
  if (filters.eventKind) params.set("event_kind", filters.eventKind);
  if (filters.runId) params.set("run_id", filters.runId);
  if (filters.since) params.set("since", filters.since);
  if (filters.until) params.set("until", filters.until);
  params.set("limit", String(LIMIT));
  return params;
}

async function fetchAuditEvents(
  filters: AuditEventFilters,
): Promise<AuditQueryData> {
  const qs = buildParams(filters).toString();
  try {
    const data = await api<AuditEventListResponse>(
      `/api/v2/audit${qs ? `?${qs}` : ""}`,
    );
    return {
      events: data.events,
      total: data.total,
      earliestEventAt: data.earliest_event_at,
      forbidden: false,
    };
  } catch (err) {
    // Community-plan orgs get 403 on unfiltered org-wide queries (SD2/SD4)
    // — that's an entitlement boundary, not a failure. Any other error
    // propagates and is surfaced as `error` below.
    if (err instanceof ApiError && err.status === 403) {
      return { events: [], total: 0, earliestEventAt: null, forbidden: true };
    }
    throw err;
  }
}

export interface AuditEventsOptions {
  enabled?: boolean;
}

export function useAuditEvents(
  filters: AuditEventFilters = {},
  options: AuditEventsOptions = {},
) {
  const enabled = options.enabled !== false;
  const query = useQuery({
    queryKey: queryKeys.audit.list(filters),
    queryFn: () => fetchAuditEvents(filters),
    enabled,
  });

  return {
    events: query.data?.events ?? [],
    total: query.data?.total ?? 0,
    earliestEventAt: query.data?.earliestEventAt ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : "Failed to load audit events"
      : null,
    forbidden: query.data?.forbidden ?? false,
    refetch: () => void query.refetch(),
  };
}
